import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade
} from '@mui/material';
import {
  BeachAccessOutlined,
  LocalHospitalOutlined,
  EventAvailableOutlined,
  Policy as PolicyIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Celebration as CelebrationIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarTodayIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import StepContent from '@mui/material/StepContent';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getCurrentLeaveYearDisplay = (policy) => {
  if (!policy) return '';
  const cycleStartMonth = policy.cycle_start_month !== undefined ? policy.cycle_start_month : 0;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  
  let cycleStart = new Date(currentYear, cycleStartMonth, 1);
  if (currentDate < cycleStart) {
    cycleStart = new Date(currentYear - 1, cycleStartMonth, 1);
  }
  
  const startYear = cycleStart.getFullYear();
  
  if (cycleStartMonth === 0) {
    return `${startYear}`;
  } else {
    const endYear = startYear + 1;
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const startMonthName = monthNames[cycleStartMonth];
    const endMonthName = monthNames[(cycleStartMonth - 1 + 12) % 12];
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  }
};

const LeaveManagement = () => {
  // Existing states...
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [employee_id, setEmployeeId] = useState('');
  const [email, setEmail] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [lastDayType, setLastDayType] = useState('full');
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [pendingDeductions, setPendingDeductions] = useState({}); // NEW: Separate state for pending
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [managerData, setManagerData] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  // NEW: States for year-end features
  const [yearEndWarning, setYearEndWarning] = useState(null);
  const [carryForwardBanner, setCarryForwardBanner] = useState(null);
  const [showCarryForwardDetails, setShowCarryForwardDetails] = useState(false);
  const [yearEndPrediction, setYearEndPrediction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refillInfo, setRefillInfo] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [leaveDetailsOpen, setLeaveDetailsOpen] = useState(false);
  const [selectedLeaveDetails, setSelectedLeaveDetails] = useState(null);
  // UPDATED: New states for duration and accrual
  const [durationPreview, setDurationPreview] = useState(null);
  const [accrualInfo, setAccrualInfo] = useState(null);
  const [company_id, setCompanyId] = useState(''); // From token
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const leaveTypeIcons = {
    annual: <BeachAccessOutlined color="primary" sx={{ fontSize: 32 }} />,
    sick: <LocalHospitalOutlined color="primary" sx={{ fontSize: 32 }} />,
    casual: <EventAvailableOutlined color="primary" sx={{ fontSize: 32 }} />,
    maternity: <CelebrationIcon color="primary" sx={{ fontSize: 32 }} />,
    paternity: <CelebrationIcon color="primary" sx={{ fontSize: 32 }} />,
    bereavement: <WarningIcon color="primary" sx={{ fontSize: 32 }} />,
    unpaid: <CheckCircleIcon color="primary" sx={{ fontSize: 32 }} />
  };

  const leaveTypes = ['annual', 'sick', 'casual', 'maternity', 'paternity', 'bereavement', 'unpaid'];

  useEffect(() => {
    const fetchCurrentUser = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/user/current`,
          { withCredentials: true });

        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);

          if (response.data.email) {
            setEmail(response.data.email);
          }

          if (response.data.companyemail) {
            setEmail(response.data.companyemail);
          }

          setEmployeeData(response.data);
          const cid = response.data.company_id || response.data.company_registration_no;
          setCompanyId(cid);

          if (cid) {
            try {
              const token = localStorage.getItem('token');
              const cmpRes = await axios.get(`${API_URL}/api/cmp/${cid}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (cmpRes.data && cmpRes.data.data) {
                setCompanyConfig({
                  dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                  timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
                });
              }
            } catch (e) {
              // Ignore
            }
          }

          await fetchManagerInfo(response.data.employee_id);
          await fetchLeaveData(response.data.employee_id);
        }
      } catch (err) {
        //console.error("Error fetching current user:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
    fetchLeavePolicy();
  }, []);

  // UPDATED: Add accrual fetch
  useEffect(() => {
    const fetchAccrual = async () => {
      const token = localStorage.getItem('token');
      if (employee_id && company_id) {
        try {
          const res = await axios.get(`${API_URL}/api/leaves/balance?employee_id=${employee_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
               
              }
            }
          );
          setAccrualInfo(res.data.accrualSchedule);
        } catch (err) {
          //console.error('Accrual fetch error:', err);
        }
      }
    };
    fetchAccrual();
  }, [employee_id, company_id]);

  // Add this effect to check for yearly refresh
  useEffect(() => {
    const checkYearlyRefresh = async () => {
      if (!employee_id) return;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;

        const response = await axios.get(
          `${API_URL}/api/leaves/yearly-refresh?employee_id=${employee_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
           
            }
          }
        );

        const data = response.data;

        // Show carry forward success banner if refresh was just applied
        if (data.refreshApplied && data.refreshResult) {
          setCarryForwardBanner({
            type: 'success',
            message: `🎉 Welcome to ${data.currentYear}! Your Leaves Have Been Refreshed`,
            details: `Your leave balances have been automatically updated.`,
            carriedForward: data.refreshResult.totalCarried,
            refillDate: data.refillDate
          });

          // Update leave balance with new values
          if (data.refreshResult.newBalances) {
            setLeaveBalance(data.refreshResult.newBalances);
          }
        }

        // Show year-end warning if in Nov/Dec
        if (data.showYearEndWarning && data.yearEndPrediction) {
          setYearEndWarning({
            type: 'warning',
            message: `⚠️ Year-End Leave Summary - ${data.currentYear}`,
            prediction: data.yearEndPrediction,
            currentYear: data.currentYear
          });
          setYearEndPrediction(data.yearEndPrediction);
        }

        // Store refill info
        if (data.refilled) {
          setRefillInfo({
            refilled: data.refilled,
            refillDate: data.refillDate,
            carryForwardInfo: data.carryForwardInfo
          });
        }

      } catch (error) {
        //console.error('Error checking yearly refresh:', error);
      }
    };

    if (employee_id) {
      checkYearlyRefresh();
    }
  }, [employee_id]);

  const fetchLeavePolicy = async () => {
    setPolicyLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const res = await axios.get(
        `${API_URL}/api/policies`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );

      const policies = res.data.policies || [];
      if (policies.length > 0) {
        const loadedPolicy = policies[0];
        setPolicy(loadedPolicy);
        const leaveTypeKeys = Object.keys(loadedPolicy).filter(key =>
          ['annual', 'sick', 'casual', 'maternity', 'paternity', 'bereavement', 'unpaid'].includes(key)
        );
        setAvailableLeaveTypes(leaveTypeKeys.map(key => ({
          value: key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')
        })));
      }
    } catch (err) {
      //console.error('Error fetching leave policy:', err);
    } finally {
      setPolicyLoading(false);
    }
  };

  const fetchLeaveData = async (empId = employee_id) => {
    const employeeIdToUse = empId || employee_id;

    if (!employeeIdToUse || !employeeIdToUse.trim()) {
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;

    setLoading(true);
    try {
      // Fetch leave balance
      const balanceRes = await axios.get(
        `${API_URL}/api/leaves/balance?employee_id=${employeeIdToUse}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      const historyRes = await axios.get(
        `${API_URL}/api/leaves/history?employee_id=${employeeIdToUse}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      const balanceData = balanceRes.data.balances || {};
      const pendingData = balanceRes.data.pendingDeductions || {}; // NEW: Separate pending
      const entitlements = balanceRes.data.entitlements || {};
      const carryForwardApplied = balanceRes.data.carryForwardApplied || false;
      setLeaveBalance(balanceData); // FIXED: Full current balance
      setPendingDeductions(pendingData); // NEW: Track pending separately

      const leaveTypeKeys = Object.keys(entitlements);
      setAvailableLeaveTypes(leaveTypeKeys.map(key => {
        const ent = entitlements[key];
        const display = ent.displayName || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const label = display.toLowerCase().endsWith('leave') ? display : `${display} Leave`;
        return {
          value: key,
          label: label
        };
      }));

      // Check for enhanced carry forward info
      if (balanceRes.data.carryForwardInfo) {
        setRefillInfo({
          refilled: true,
          carryForwardInfo: balanceRes.data.carryForwardInfo,
          refillDate: balanceRes.data.lastPolicySync
        });
      }

      const processedLeaves = (historyRes.data || []).map(leave => {
        const processed = {
          ...leave,
          leaveType: leave.leaveType || leave.leave_type || 'N/A',
          startDate: leave.startDate || leave.start_date,
          endDate: leave.endDate || leave.end_date,
          reason: leave.reason || 'No reason provided',
          status: leave.status || 'Pending',
          manager_comment: leave.manager_comment || leave.managerComment || '',
          lastDay: leave.lastDay || leave.last_day || false,
          duration: leave.duration // Assume from backend
        };
        return processed;
      });

      setLeaves(processedLeaves);
    } catch (error) {
      //console.error('Fetch Error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to fetch leave data';
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagerInfo = async (employeeId) => {
    if (!employeeId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;

    try {
      const roleResponse = await axios.get(
        `${API_URL}/api/roles/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );

      if (roleResponse.data && roleResponse.data.data) {
        const roleData = roleResponse.data.data;

        if (roleData.reportingManager) {
          setManagerEmail(roleData.reportingManager);
          setManagerData(roleData);
          return;
        }
      }

      const managerResponse = await axios.get(
        `${API_URL}/api/leaves/manager/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          },
          withCredentials: true
        }
      );

      if (managerResponse.data && managerResponse.data.manager_email) {
        setManagerEmail(managerResponse.data.manager_email);
        setManagerData(managerResponse.data);
      }
    } catch (roleErr) {
      //console.error("Error fetching employee's manager:", roleErr);
    }
  };

  // UPDATED: Date change handler for preview
  const handleDateChange = async () => {
    if (startDate && endDate && company_id) {
      try {
        const res = await axios.post(`${API_URL}/api/leaves/duration-preview`, {
          company_id,
          startDate,
          endDate,
          lastDayType
        });
        setDurationPreview(res.data);
      } catch (err) {
        //console.error('Preview error:', err);
      }
    } else {
      setDurationPreview(null);
    }
  };

  useEffect(() => {
    handleDateChange();
  }, [startDate, endDate, lastDayType]);

  const validateDates = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      alert('Please select valid dates');
      return false;
    }

    if (start > end) {
      alert('End date cannot be before start date');
      return false;
    }


    return true;
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate || !selectedLeaveType || !reason || !email || !managerEmail) {
      alert('Please fill all required fields');
      return;
    }
    if (!validateDates()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split(".")[1]));
      const tokenEmployeeId = payload.employee_id;
      const company_id = payload.company_id;
      const employeeIdToUse = employee_id || tokenEmployeeId;
      if (!employeeIdToUse) {
        alert("Employee ID not found. Please refresh and try again.");
        return;
      }

      const formData = new FormData();
      formData.append('employeeId', employeeIdToUse);
      formData.append('email', email);
      formData.append('managerEmail', managerEmail);
      formData.append('leaveType', selectedLeaveType);
      formData.append('startDate', startDate);
      formData.append('endDate', endDate);
      formData.append('reason', reason);
      formData.append('lastDayType', lastDayType);
      if (uploadedFile) {
        formData.append('leaveDocument', uploadedFile);
      }

      const response = await axios.post(
        `${API_URL}/api/leaves/apply`,
        formData,
        {
        
          headers: { 'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
           },
        }
      );
      await fetchLeaveData();
      setOpenDialog(false);
      setSelectedLeaveType('');
      setStartDate('');
      setEndDate('');
      setReason('');
      setLastDayType('full');
      setUploadedFile(null);
      alert('Leave submitted successfully! Email notifications have been sent.');
    } catch (error) {
      //console.error('Submission Error:', error);
      let errorMessage = 'Failed to submit leave application';
      if (error.response) {
        errorMessage = error.response.data.error ||
          error.response.data.details ||
          'Server returned an error';
      } else if (error.request) {
        errorMessage = 'No response received from server. Please check your network connection.';
      } else {
        errorMessage = error.message || 'An unknown error occurred';
      }
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper Functions
  const hasLeaveData = (leaveData) => {
    if (!leaveData) return false;
    return leaveData.days || leaveData.paid_status;
  };

  const getLeaveBalanceStyle = (type) => {
    switch (type) {
      case 'annual':
        return {
          backgroundColor: '#e3f2fd',
          borderLeft: '4px solid #2196f3'
        };
      case 'sick':
        return {
          backgroundColor: '#fff3e0',
          borderLeft: '4px solid #ff9800'
        };
      case 'casual':
        return {
          backgroundColor: '#f3e5f5',
          borderLeft: '4px solid #9c27b0'
        };
      case 'unpaid':
        return {
          backgroundColor: '#f5f5f5',
          borderLeft: '4px solid #9e9e9e'
        };
      default:
        return {};
    }
  };

  const getLeaveTypeInfo = (type) => {
    if (!policy) return null;

    try {
      if (policy[type]) return JSON.parse(policy[type]);
      if (policy.custom_policies) {
        const parsed = typeof policy.custom_policies === 'string'
          ? JSON.parse(policy.custom_policies)
          : policy.custom_policies;
        if (parsed && parsed[type]) return parsed[type];
      }
      return null;
    } catch (err) {
      //console.error(`Error parsing ${type} leave data:`, err);
      return null;
    }
  };

  const getLeaveIcon = (type) => {
    switch (type) {
      case 'annual': return <BeachAccessOutlined color="primary" sx={{ fontSize: 32 }} />;
      case 'sick': return <LocalHospitalOutlined color="primary" sx={{ fontSize: 32 }} />;
      case 'casual': return <EventAvailableOutlined color="primary" sx={{ fontSize: 32 }} />;
      case 'unpaid': return <CheckCircleIcon color="primary" sx={{ fontSize: 32 }} />;
      default: return <CheckCircleIcon color="primary" sx={{ fontSize: 32 }} />;
    }
  };

  const getLeaveButtonColor = (type) => {
    switch (type) {
      case 'annual': return '#2196f3';
      case 'sick': return '#ff9800';
      case 'casual': return '#9c27b0';
      case 'unpaid': return '#9e9e9e';
      default: return '#4caf50';
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      if (isNaN(new Date(dateValue).getTime())) return 'Invalid Date';
      return dayjs(dateValue).tz(companyConfig.timezone).format(companyConfig.dateFormat);
    } catch (err) {
      return 'Invalid Date';
    }
  };

  const getLeaveTypeDisplay = (leave) => {
    const type = leave.leaveType || leave.leave_type;
    if (!type || type === 'N/A') return 'Not Specified';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const handleOpenLeaveDetails = (leaveType) => {
    const typeInfo = getLeaveTypeInfo(leaveType);
    if (!typeInfo) return;
    const predictionData = yearEndPrediction?.predictions?.[leaveType] || {};
    const currentBal = leaveBalance[leaveType] || 0;
    const pendingBal = pendingDeductions[leaveType] || 0;
    setSelectedLeaveDetails({
      type: leaveType,
      ...typeInfo,
      currentBalance: currentBal,
      pendingBalance: pendingBal, // NEW: For details
      carryForwardInfo: refillInfo?.carryForwardInfo?.[leaveType],
      predictionData
    });
    setLeaveDetailsOpen(true);
  };

  const handleCloseLeaveDetails = () => {
    setLeaveDetailsOpen(false);
    setSelectedLeaveDetails(null);
  };

  // FIXED: Updated renderLeaveTypeCard - Show full current + pending note
  const renderLeaveTypeCard = (type, label, icon) => {
    const typeInfo = getLeaveTypeInfo(type);
    if (!typeInfo || !typeInfo.days) return null;
    const currentBal = leaveBalance[type] || 0; // FIXED: Full current
    const pendingBal = pendingDeductions[type] || 0; // NEW: Pending
    const isUnlimited = type === 'unpaid';

    // Separate actual carried (from last year, only if >0) vs. policy max
    const actualCarried = refillInfo?.carryForwardInfo?.[type]?.carried || 0;
    const maxAllowed = refillInfo?.carryForwardInfo?.[type]?.maxAllowed || 0;
    const carryForwardRule = typeInfo.carry_forward || 'Not allowed';
    const canCarryForward = !carryForwardRule.toLowerCase().includes('not allowed') && maxAllowed > 0;

    return (
      <Grid item xs={12} key={type}>
        <Card
          sx={{
            height: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-4px)',
              cursor: 'pointer'
            },
            ...getLeaveBalanceStyle(type)
          }}
          onClick={() => handleOpenLeaveDetails(type)}
        >
          <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {icon}
              <Box sx={{ ml: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                {canCarryForward && maxAllowed > 0 && (
                  <Chip
                    label={`Max Carry: ${maxAllowed} days`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ mt: 0.5, fontSize: '0.7rem' }}
                  />
                )}
                {actualCarried > 0 && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                    +{actualCarried} carried forward (from last year)
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {isUnlimited ? 'Unlimited' : `${currentBal} days`}
              </Typography>

              {pendingBal > 0 && (
                <Chip
                  label={`${pendingBal} pending`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mt: 0.5, fontSize: '0.7rem' }}
                />
              )}

              {!isUnlimited && typeInfo.days && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"          // 👈 this makes it move to next line
                  sx={{ mt: 0.5 }}
                >
                  Base: {typeInfo.days} days
                </Typography>
              )}
            </Box>

          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderBalanceCards = () => {
    if (!policy || Object.keys(leaveBalance).length === 0) return null;

    return (
      <>
        {availableLeaveTypes.map(typeObj => {
          const type = typeObj.value;
          const typeInfo = getLeaveTypeInfo(type);
          if (!typeInfo) return null;
          const label = typeObj.label;
          const icon = getLeaveIcon(type);
          return renderLeaveTypeCard(type, label, icon);
        })}
      </>
    );
  };

  // Render Carry Forward Success Banner
  const renderCarryForwardBanner = () => {
    if (!carryForwardBanner) return null;

    const { type, message, details, carriedForward, refillDate } = carryForwardBanner;

    return (
      <Fade in={true}>
        <Alert
          severity={type}
          icon={<CelebrationIcon />}
          sx={{ mb: 3 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setCarryForwardBanner(null)}
            >
              <ExpandLessIcon />
            </IconButton>
          }
        >
          <Typography variant="h6" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body2" paragraph>
            {details}
          </Typography>
          {carriedForward && Object.keys(carriedForward).length > 0 && (
            <Box sx={{ mt: 1 }}>
              {Object.entries(carriedForward).map(([type, days]) => (
                days > 0 && (
                  <Chip
                    key={type}
                    label={`${type}: +${days} days`}
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )
              ))}
            </Box>
          )}
          {refillDate && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Refilled on: {new Date(refillDate).toLocaleDateString()}
            </Typography>
          )}
        </Alert>
      </Fade>
    );
  };

  // Render Year-End Warning Banner
  const renderYearEndWarning = () => {
    if (!yearEndWarning) return null;

    const { type, message, prediction, currentYear } = yearEndWarning;

    return (
      <Fade in={true}>
        <Alert
          severity={type}
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
          action={
            <Box>
              <IconButton
                aria-label="details"
                color="inherit"
                size="small"
                onClick={() => setShowCarryForwardDetails(!showCarryForwardDetails)}
              >
                {showCarryForwardDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setYearEndWarning(null)}
              >
                <ExpandLessIcon />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="h6" gutterBottom>
            {message}
          </Typography>
          <Typography variant="body2">
            As of December 31st:
          </Typography>

          {prediction?.summary && (
            <Box sx={{ mt: 1 }}>
              {prediction.summary.totalToCarry > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 16, mr: 1 }} />
                  <Typography variant="body2">
                    {prediction.summary.totalToCarry} days will carry forward to {currentYear + 1} (from this year's unused)
                  </Typography>
                </Box>
              )}
              {prediction.summary.totalToExpire > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WarningIcon color="error" sx={{ fontSize: 16, mr: 1 }} />
                  <Typography variant="body2" color="error.main">
                    {prediction.summary.totalToExpire} days will expire (use them before Dec 31st!)
                  </Typography>
                </Box>
              )}
              {prediction.summary.warning && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {prediction.summary.warning}
                </Typography>
              )}
            </Box>
          )}

          <Collapse in={showCarryForwardDetails}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Detailed Prediction:
              </Typography>
              {prediction?.predictions && Object.entries(prediction.predictions).map(([type, data]) => (
                <Box key={type} sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold" textTransform="capitalize">
                    {type} Leave:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    {data.totalRemaining} days remaining • {data.willCarry} will carry to {currentYear + 1} • {data.willExpire} will expire
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Alert>
      </Fade>
    );
  };

  // FIXED: Render Leave Details Dialog (Added pending row)
  const renderLeaveDetailsDialog = () => {
    if (!selectedLeaveDetails) return null;
    const { type, days, carry_forward, paid_status, currentBalance, pendingBalance, carryForwardInfo, predictionData } = selectedLeaveDetails;
    const displayName = type.charAt(0).toUpperCase() + type.slice(1);
    const actualCarried = carryForwardInfo?.carried || 0;
    const maxAllowed = carryForwardInfo?.maxAllowed || 0;
    return (
      <Dialog
        open={leaveDetailsOpen}
        onClose={handleCloseLeaveDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getLeaveIcon(type)}
            <Typography variant="h6">{displayName} Leave Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Current Balance
                </Typography>
                <Typography variant="h3" color="primary" align="center">
                  {type === 'unpaid' ? 'Unlimited' : `${currentBalance} days`}
                </Typography>
                {/* NEW: Pending note in details */}
                {pendingBalance > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">{pendingBalance} days reserved (pending approval)</Typography>
                  </Alert>
                )}
                {type !== 'unpaid' && (
                  <Typography variant="body2" align="center" color="text.secondary">
                    Base entitlement: {days} days
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Carry Forward Rules
                </Typography>
                <Typography variant="body2" paragraph>
                  {carry_forward || 'Not applicable'}
                </Typography>
                {actualCarried > 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Actual Carried (from last year):</strong> {actualCarried} days
                    </Typography>
                  </Alert>
                )}
                {maxAllowed > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Policy Max Allowed:</strong> {maxAllowed} days per year
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Year-End Prediction
              </Typography>
              {predictionData && Object.keys(predictionData).length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="right">Max Allowed</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Base Entitlement</TableCell>
                        <TableCell align="right">{predictionData.baseThisYear} days</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell>
                          <Chip label={displayName} size="small" color="primary" />
                        </TableCell>
                      </TableRow>
                      {predictionData.usedThisYear > 0 && (
                        <TableRow>
                          <TableCell>Used This Year</TableCell>
                          <TableCell align="right">{predictionData.usedThisYear} days</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell>
                            <Chip label="Consumed" size="small" />
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell>Unused This Year</TableCell>
                        <TableCell align="right">{predictionData.unusedThisYear} days</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell>
                          <Chip label="Current Unused" size="small" color="info" variant="outlined" />
                        </TableCell>
                      </TableRow>
                      {predictionData.priorRemaining > 0 && (
                        <TableRow>
                          <TableCell>Prior Remaining</TableCell>
                          <TableCell align="right">{predictionData.priorRemaining} days</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell>
                            <Chip label="From Last Year" size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell>Total Remaining (incl. prior)</TableCell>
                        <TableCell align="right">{predictionData.totalRemaining} days</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell>
                          <Chip
                            label="Available"
                            size="small"
                            color={predictionData.totalRemaining > 0 ? "success" : "default"}
                          />
                        </TableCell>
                      </TableRow>
                      {predictionData.canCarryForward ? (
                        <>
                          <TableRow>
                            <TableCell>Will Carry Forward</TableCell>
                            <TableCell align="right">{predictionData.willCarry} days</TableCell>
                            <TableCell align="right">{predictionData.carryLimit} days</TableCell>
                            <TableCell>
                              <Chip label="From Current Unused" size="small" color="success" variant="outlined" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Will Expire</TableCell>
                            <TableCell align="right">{predictionData.willExpire} days</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell>
                              <Chip
                                label="Prior + Excess Current"
                                size="small"
                                color={predictionData.willExpire > 0 ? "error" : "default"}
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell>Will Carry Forward</TableCell>
                          <TableCell align="right">0 days</TableCell>
                          <TableCell align="right">0 days</TableCell>
                          <TableCell>
                            <Chip label="No Carry" size="small" color="default" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  Year-end prediction available in Nov/Dec
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>Note:</strong> {carry_forward ?
                    `Up to ${maxAllowed} days from this year's unused can be carried forward (prior remaining expires).` :
                    'This leave type does not carry forward (all remaining expires/resets).'}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeaveDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // FIXED: Render "How It Works" (Clarify prior vs current)
  const renderHowItWorks = () => (
    <Collapse in={showHowItWorks}>
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            <HelpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            How Carry Forward Works
          </Typography>
          <IconButton onClick={() => setShowHowItWorks(false)} size="small">
            <ExpandLessIcon />
          </IconButton>
        </Box>

        <Stepper orientation="vertical">
          <Step active={true}>
            <StepLabel>
              <Typography variant="subtitle2" fontWeight="bold">Year-End Calculation (Dec 31)</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2">
                Unused this year = base entitlement - used this year (leaves started this year).<br />
                Prior carried remaining expires fully.<br />
                Only unused this year can carry forward (up to max).
              </Typography>
            </StepContent>
          </Step>

          <Step active={true}>
            <StepLabel>
              <Typography variant="subtitle2" fontWeight="bold">Policy Application</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2">
                Carry forward rules (capped at max from this year's unused):
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li><strong>Annual/Sick:</strong> ✅ Max 5 days from this year's unused</li>
                  <li><strong>Casual:</strong> ✅ Max 5 days from this year's unused (if allowed)</li>
                  <li><strong>Maternity/etc.:</strong> ❌ No carry (resets)</li>
                </Box>
              </Typography>
            </StepContent>
          </Step>

          <Step active={true}>
            <StepLabel>
              <Typography variant="subtitle2" fontWeight="bold">New Year Balance (Jan 1)</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2">
                Balances refreshed:
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li>New base entitlement added</li>
                  <li>Carry from this year's unused (up to max) added</li>
                  <li>Prior remaining & excess current expire</li>
                  <li>Notification sent</li>
                </Box>
              </Typography>
            </StepContent>
          </Step>
        </Stepper>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Example:</strong> Base 14 + prior 5 =19 total (used 0). Unused this year=14, carry min(14,5)=5, expire prior 5 + excess 9=14. Next: 14 +5=19.
          </Typography>
        </Box>
      </Paper>
    </Collapse>
  );

  // Render Info Button
  const renderInfoButton = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Button
        startIcon={<HelpIcon />}
        onClick={() => setShowHowItWorks(!showHowItWorks)}
        size="small"
        variant="outlined"
      >
        {showHowItWorks ? 'Hide How It Works' : 'How Carry Forward Works'}
      </Button>
    </Box>
  );

  // Main return statement
  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
  <Paper sx={{ p: 3, mb: 3 }}>
    <Typography variant="h4" gutterBottom fontWeight="bold">
      Apply for Leave
    </Typography>

    {policy && (
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ mb: 1, fontWeight: 500 }}
      >
        Active Leave Year Cycle: {getCurrentLeaveYearDisplay(policy)}
      </Typography>
    )}

    <Box sx={{ display: 'flex', mt: 2 }}>
      <TextField
        disabled
        label="Employee ID"
        value={employee_id}
        InputProps={{ readOnly: true }}
        sx={{ width: 300 }}
      />

      <Button
        variant="contained"
        onClick={() => setOpenDialog(true)}
        disabled={!employee_id || !policy || availableLeaveTypes.length === 0}
        size="medium"
        sx={{ px: 4, py: 1.5, ml:'auto' }}
      >
        Apply
      </Button>
    </Box>

      
      </Paper>
      {/* NEW: Add banners and info sections */}
      {renderCarryForwardBanner()}
      {renderYearEndWarning()}
      {renderInfoButton()}
      {renderHowItWorks()}
      {!policy && !policyLoading && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
          No leave policy has been configured yet. Please contact HR to set up the leave policy.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h5" gutterBottom fontWeight="600">Leave History</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : leaves.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  No leave history found
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, mb: 2 }}>
                {leaves.map(leave => (
                  <Paper key={leave.id} sx={{
                    p: 2, my: 1, borderLeft: '4px solid', borderColor:
                      leave.status === 'Approved' ? 'success.main' :
                        leave.status === 'Rejected' ? 'error.main' :
                          leave.status === 'Cancelled' ? 'grey.500' : 'warning.main'
                  }}>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">Leave Type:</Typography>
                        <Typography variant="body1" fontWeight="medium" textTransform="capitalize">
                          {getLeaveTypeDisplay(leave)}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">Duration:</Typography>
                        <Typography variant="body1">
                          {formatDate(leave.startDate)} to {formatDate(leave.endDate)}
                          {leave.lastDay && ' (last day half)'}
                          {leave.duration && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {leave.duration.workingDays.toFixed(1)} working days
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="subtitle2" color="text.secondary">Status:</Typography>
                        <Chip
                          label={leave.status || 'Pending'}
                          color={
                            leave.status === 'Approved' ? 'success' :
                              leave.status === 'Rejected' ? 'error' :
                                leave.status === 'Cancelled' ? 'default' : 'warning'
                          }
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Grid>
                      <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="subtitle2" color="text.secondary">Reason:</Typography>
                          <Typography variant="body2">{leave.reason || 'No reason provided'}</Typography>
                        </Box>
                      </Grid>
                      {leave.manager_comment && (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Manager Comment:</Typography>
                          <Typography variant="body2" fontStyle="italic">{leave.manager_comment}</Typography>
                        </Grid>
                      )}
                      {leave.document_url && (
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            href={`${API_URL}/${leave.document_url}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            📎 View Document
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="600">Leave Balance</Typography>
              {policyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    {renderBalanceCards()}
                    {Object.keys(leaveBalance).length === 0 && !policy && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No policy configured. Balances will be available once policy is set.
                      </Alert>
                    )}
                  </Grid>
                  {accrualInfo && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">Next accrual: {accrualInfo.nextDate} (+{accrualInfo.amount} days)</Typography>
                    </Alert>
                  )}
                </>
              )}
            </Paper>

         
          </Box>
        </Grid>
      </Grid>
      {/* Leave Details Dialog */}
      {renderLeaveDetailsDialog()}
      {/* Existing Leave Application Dialog - FIXED: Show available (current) with pending note */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedLeaveType('');
          setUploadedFile(null);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">Apply for Leave</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Left Side - Leave Type Buttons and Form */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Leave Type Selection Buttons */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom fontWeight="600" sx={{ mb: 1.5 }}>
                    Select Leave Type
                  </Typography>
                  <Grid container spacing={1.5}>
                    {availableLeaveTypes.map(({ value, label }) => {
                      const typeInfo = getLeaveTypeInfo(value);
                      if (!typeInfo || !typeInfo.days) return null;

                      const currentBal = leaveBalance[value] || 0; // FIXED: Full current
                      const pendingBal = pendingDeductions[value] || 0; // NEW: Pending
                      const isUnlimited = value === 'unpaid';
                      const isSelected = selectedLeaveType === value;
                      // Light pastel colors for buttons
                      const lightColors = {
                        annual: { bg: '#BBDEFB', hover: '#BBDEFB', border: '#64B5F6', text: '#1565C0' },
                        sick: { bg: '#FFF3E0', hover: '#FFE0B2', border: '#FFB74D', text: '#E65100' },
                        casual: { bg: '#F3E5F5', hover: '#E1BEE7', border: '#BA68C8', text: '#6A1B9A' },
                        unpaid: { bg: '#F5F5F5', hover: '#E0E0E0', border: '#9E9E9E', text: '#424242' }
                      };
                      const colorScheme = lightColors[value] || lightColors.unpaid;
                      return (
                        <Grid item xs={6} key={value}>
                          <Button
                            fullWidth
                            onClick={() => setSelectedLeaveType(value)}
                            sx={{
                              height: 60,
                              flexDirection: 'column',
                              justifyContent: 'center',
                              gap: 0.3,
                              border: '1.5px solid',
                              borderColor: isSelected ? colorScheme.border : '#e0e0e0',
                              backgroundColor: isSelected ? colorScheme.bg : `${colorScheme.bg}55`,
                              color: isSelected ? colorScheme.text : '#555',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? 2 : 0,
                              '&:hover': {
                                backgroundColor: colorScheme.hover,
                                borderColor: colorScheme.border,
                                boxShadow: 1,
                              },
                            }}
                          >
                            {React.cloneElement(getLeaveIcon(value), { sx: { fontSize: 20 } })}
                            <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.8rem' }}>
                              {label}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {isUnlimited ? 'Unlimited' : `${currentBal} days${pendingBal > 0 ? ` (${pendingBal} pending)` : ''}`} {/* FIXED: Show full + pending note */}
                            </Typography>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
                {/* Application Form */}
                <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="600" sx={{ mb: 2 }}>
                    Application Details
                  </Typography>

                  {selectedLeaveType ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {/* FIXED: Alert shows full current + pending note */}
                        <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            <strong>Selected:</strong> {availableLeaveTypes.find(t => t.value === selectedLeaveType)?.label}
                            {' • '}
                            <strong>Available:</strong> {leaveBalance[selectedLeaveType] || 0} days
                            {pendingDeductions[selectedLeaveType] > 0 && (
                              <span> ({pendingDeductions[selectedLeaveType]} pending reservation)</span>
                            )}
                          </Typography>
                        </Alert>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Employee ID"
                          value={employee_id}
                          disabled
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          fullWidth
                          required
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Manager Email"
                          type="email"
                          value={managerEmail}
                          onChange={(e) => setManagerEmail(e.target.value)}
                          fullWidth
                          required
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Start Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={startDate}
                          onChange={(e) => { setStartDate(e.target.value); handleDateChange(); }}
                          fullWidth
                          required
                          size="small"
                          inputProps={{

                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="End Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={endDate}
                          onChange={(e) => { setEndDate(e.target.value); handleDateChange(); }}
                          fullWidth
                          required
                          size="small"
                          inputProps={{

                          }}
                        />
                      </Grid>
                      {/* UPDATED: Duration Preview */}
                      {startDate && endDate && durationPreview && (
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ py: 1 }}>
                            <Typography variant="body2">
                              Duration Preview: {durationPreview.workingDays.toFixed(1)} working days
                              (Weekends: {durationPreview.weekendDays}, Holidays: {durationPreview.holidayDays})
                            </Typography>
                            {accrualInfo && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                Next Accrual: {accrualInfo.nextDate} (+{accrualInfo.amount} days)
                              </Typography>
                            )}
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Last Day Type</InputLabel>
                          <Select
                            label="Last Day Type"
                            value={lastDayType}
                            onChange={(e) => { setLastDayType(e.target.value); handleDateChange(); }}
                          >
                            <MenuItem value="full">Full Day</MenuItem>
                            <MenuItem value="half">Half Day</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Reason"
                          multiline
                          rows={3}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          fullWidth
                          required
                          size="small"
                          placeholder="Please provide reason for leave..."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          Upload Document (Optional)
                          <input
                            type="file"
                            hidden
                            accept=".pdf, .jpg, .jpeg, .png"
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                setUploadedFile(e.target.files[0]);
                              }
                            }}
                          />
                        </Button>
                        {uploadedFile && (
                          <Typography variant="caption" display="block" sx={{ mt: 1, ml: 1, color: "success.main" }}>
                            Selected file: {uploadedFile.name}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Please select a leave type above
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  maxHeight: '600px',
                  overflow: 'hidden',
                  border: '1px solid #d0d0d0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {policy ? (
                  <Box sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Document Header - Like a real document */}
                    <Box sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      p: 2.5,
                      textAlign: 'center'
                    }}>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        sx={{ mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        {policy.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Effective from {new Date(policy.effective_date).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    {/* Document Content */}
                    <Box sx={{
                      flex: 1,
                      overflow: 'auto',
                      backgroundColor: 'white',
                      p: 2
                    }}>
                      <Grid container spacing={2}>
                        {availableLeaveTypes.map(({ value: type }) => {
                          let leaveData = null;
                          try {
                            leaveData = policy[type] ? JSON.parse(policy[type]) : null;
                          } catch (e) {
                            //console.error(`Error parsing ${type} leave data:`, e);
                          }
                          if (!hasLeaveData(leaveData)) return null;

                          const displayFields = [];
                          if (leaveData.days) {
                            displayFields.push({ label: 'Days per Year', value: leaveData.days });
                          }
                          if (leaveData.paid_status) {
                            displayFields.push({ label: 'Paid Status', value: leaveData.paid_status.replace('_', ' ') });
                          }
                          if (leaveData.carry_forward) {
                            displayFields.push({ label: 'Carry Forward', value: leaveData.carry_forward });
                          }

                          return (
                            <Grid item xs={12} key={type}>
                              <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  {leaveTypeIcons[type] || <PolicyIcon sx={{ fontSize: 24, color: 'primary.main' }} />}
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)} Leave
                                  </Typography>
                                </Box>
                                {displayFields.map(({ label, value }) => (
                                  <Typography key={label} sx={{ mb: 0.5, fontSize: '0.95rem' }}>
                                    <strong>{label}:</strong> {value}
                                  </Typography>
                                ))}
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>

                      {policy.general_rules && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 1 }}>
                            <Chip label="General Rules" size="small" />
                          </Divider>
                          <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {policy.general_rules}
                            </Typography>
                          </Paper>
                        </Box>
                      )}


                    </Box>

                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="info" icon={<InfoIcon />}>
                      No leave policy configured yet. Please contact HR.
                    </Alert>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setSelectedLeaveType('');
            }}
            color="primary"
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="large"
            disabled={loading || !selectedLeaveType || !startDate || !endDate || !reason || !email || !managerEmail}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};


export default LeaveManagement;